import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Lightning, CheckCircle, Heart } from "@phosphor-icons/react";

const ROLES = [
  { value: "nonprofit", label: "Nonprofit founder / staff" },
  { value: "for_profit", label: "Business owner" },
  { value: "aspiring_founder", label: "Aspiring founder (pre-501(c)(3))" },
  { value: "other", label: "Something else" },
];

const Feedback = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "",
    one_liner: "",
    missing_or_dealbreaker: "",
    nps_score: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const npsParam = new URLSearchParams(window.location.search).get("source");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setNps = (n) => setForm((f) => ({ ...f, nps_score: String(n) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.one_liner.trim() && !form.missing_or_dealbreaker.trim()) {
      toast.error("Please share at least one piece of feedback.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        ...form,
        source: npsParam || "feedback-page",
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-6" data-testid="feedback-thanks">
        <Card className="max-w-xl w-full bg-zinc-900 border-emerald-500/30">
          <CardContent className="p-10 text-center">
            <Heart weight="fill" size={48} className="mx-auto text-emerald-400 mb-5" />
            <h1 className="text-3xl font-black tracking-tight mb-3">Thank you.</h1>
            <p className="text-zinc-300 text-lg leading-relaxed mb-6">
              This lands directly in the founder's inbox — and your name (if you shared it) is now on the very short list of people who shaped what FlashGrants becomes.
            </p>
            <p className="text-zinc-500 text-sm mb-8">
              If you left an email, expect a personal reply within 48 hours. No marketing list, no automated drip.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/">
                <Button className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold w-full sm:w-auto" data-testid="feedback-back-home">
                  Back to FlashGrants
                </Button>
              </Link>
              <Link to="/start-a-nonprofit-in-sc">
                <Button variant="outline" className="border-zinc-700 text-zinc-200 hover:bg-zinc-800 w-full sm:w-auto">
                  Read the free 501(c)(3) roadmap →
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="feedback-page">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <Link to="/" className="text-zinc-500 hover:text-emerald-400 text-sm flex items-center gap-1 mb-6" data-testid="feedback-back-link">
          <Lightning weight="fill" size={14} /> FlashGrants
        </Link>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 mb-5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          <span className="text-[11px] font-bold tracking-[0.18em] text-emerald-300 uppercase">
            Tell us what's missing
          </span>
        </div>

        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-4">
          Help us build a platform <span className="text-emerald-400">SC actually needs.</span>
        </h1>
        <p className="text-zinc-400 text-lg leading-relaxed mb-10">
          FlashGrants is small and listening. Every answer below is read by the founder personally — usually within 24 hours.
        </p>

        <form onSubmit={submit} className="space-y-7" data-testid="feedback-form">
          {/* Name + Email — both optional */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="fb-name" className="text-zinc-300 text-sm">Your name <span className="text-zinc-600 text-xs ml-1">(optional)</span></Label>
              <Input
                id="fb-name"
                value={form.name}
                onChange={update("name")}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1.5"
                placeholder="First and last"
                data-testid="feedback-name"
              />
            </div>
            <div>
              <Label htmlFor="fb-email" className="text-zinc-300 text-sm">Email <span className="text-zinc-600 text-xs ml-1">(optional — only if you want a reply)</span></Label>
              <Input
                id="fb-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1.5"
                placeholder="you@example.com"
                data-testid="feedback-email"
              />
            </div>
          </div>

          {/* Role */}
          <div>
            <Label className="text-zinc-300 text-sm">What best describes you?</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              {ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, role: r.value }))}
                  className={`text-left px-4 py-3 rounded-md border text-sm transition ${
                    form.role === r.value
                      ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-200"
                      : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700"
                  }`}
                  data-testid={`feedback-role-${r.value}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* One-liner */}
          <div>
            <Label htmlFor="fb-oneliner" className="text-zinc-300 text-sm">In one line — what did you think of FlashGrants?</Label>
            <Textarea
              id="fb-oneliner"
              value={form.one_liner}
              onChange={update("one_liner")}
              rows={2}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1.5 resize-none"
              placeholder='e.g. "Saved me hours" / "Confusing at first" / "I want to use it but..."'
              data-testid="feedback-oneliner"
            />
          </div>

          {/* Missing / dealbreaker */}
          <div>
            <Label htmlFor="fb-missing" className="text-zinc-300 text-sm">What's missing — or what would make this a no-brainer for you?</Label>
            <Textarea
              id="fb-missing"
              value={form.missing_or_dealbreaker}
              onChange={update("missing_or_dealbreaker")}
              rows={4}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1.5 resize-none"
              placeholder="The honest version. We can take it."
              data-testid="feedback-missing"
            />
          </div>

          {/* NPS */}
          <div>
            <Label className="text-zinc-300 text-sm">
              How likely are you to recommend FlashGrants to a fellow SC founder?
            </Label>
            <div className="flex items-center justify-between gap-1 mt-3" data-testid="feedback-nps">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setNps(n)}
                  className={`flex-1 h-10 rounded-md border text-sm font-bold transition ${
                    String(n) === form.nps_score
                      ? "bg-emerald-500 border-emerald-400 text-black"
                      : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700"
                  }`}
                  data-testid={`feedback-nps-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-[11px] text-zinc-600 mt-1 px-1">
              <span>Not at all</span>
              <span>Absolutely</span>
            </div>
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold py-6 text-base"
              data-testid="feedback-submit"
            >
              {submitting ? "Sending…" : "Send feedback to the founder"}
            </Button>
            <p className="text-xs text-zinc-600 mt-3 flex items-center justify-center gap-1.5">
              <CheckCircle weight="fill" size={12} className="text-emerald-500" />
              No marketing list. No automated drip. Just real ears on the other side.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Feedback;
